/**
 * @file: useCreateOrderRequest.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { siteInspectionStagedToCommitPayload } from '@euler/app/flows/task/functions/task-detail/util';
import { CreateOrderRequest, TroubleLightInfo } from '@euler/model';
import {
  CarwashType,
  InspectionOrderPriority,
  InspectionOrderType,
  OrderReturnType,
  OrderSourceType,
  SiteInspectionType,
} from '@euler/model/enum';
import { normalizedOrderTags } from '@euler/utils';
import { formatDateTime } from '@euler/utils/formatters';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { normalizedExtendedPropertiesFromOrderExtra } from '../../../../functions/order/extendedPropertyUtil';
import { useOrderFlowContext } from '../OrderFlowContext';
import { vehicleIdentityFromForm } from './vinHelper';

export function useCreateOrderRequest() {
  const {
    formController: { form },
    orderDetail$,
    lastVehicleInfo$,
    ocrImageInfo,
    storeConfiguration,
  } = useOrderFlowContext();
  const [detail] = useBehaviorSubject(orderDetail$);
  const user = useCurrentUserDangerously();
  const [lpnOcrInfo] = useBehaviorSubject(ocrImageInfo.licensePlateNo$);
  const [vinOcrInfo] = useBehaviorSubject(ocrImageInfo.vin$);
  const [lastVehicleInfo] = useBehaviorSubject(lastVehicleInfo$);

  return usePersistFn(
    (args?: { shouldFinishPreInspection?: boolean }): CreateOrderRequest => {
      return {
        ...args,
        orgId: user.org.id,
        storeId: user.store.id,
        orderType: InspectionOrderType.Basic,
        licensePlateNo: form.licensePlateNo.value,
        ...vehicleIdentityFromForm({
          isNonStandardVin: form.isNonStandardVin.value,
          vin: form.vin.value,
          chassisNo: form.chassisNo.value,
        }),
        tags: normalizedOrderTags(form.tags.value?.join(',') ?? ''),
        source: {
          type: OrderSourceType.Default,
          serviceAgentId: form.serviceAgentInfo.value?.serviceAgentId ?? 0,
          serviceAgentName: form.serviceAgentInfo.value?.name ?? '',
          requestMemberId: user.user.memberId,
        },
        vehicleMileage: form.mileage.value,
        vehicleName: form.vehicleInfo.value?.vehicleName ?? '',
        vehicleBrandName: form.vehicleInfo.value?.vehicleBrandName ?? '',
        vehicleModel: form.vehicleInfo.value?.vehicleModel,
        vehicleModelYear: form.vehicleInfo.value?.vehicleModelYear,
        vehicleDisplacement: form.vehicleInfo.value?.vehicleDisplacement,
        vehicleManufacturer: form.vehicleInfo.value?.vehicleManufacturer,
        vehicleFuelType: form.vehicleInfo.value?.vehicleFuelType,
        vehicleTrimLevel: form.vehicleInfo.value?.vehicleTrimLevel,
        dashboardImgUrl: form.dashboardImgUrl.value,
        licensePlateNoImgUrl: lpnOcrInfo?.imageUrl,
        licensePlateNoTimestamp: lpnOcrInfo?.timestamp,
        vinImgUrl: vinOcrInfo?.imageUrl,
        vinTimestamp: vinOcrInfo?.timestamp,
        customerName: form.customerName.value,
        customerMobile: form.customerMobile.value,
        customerType: form.customerType.value,
        customerIsOnSite: form.returnType.value === OrderReturnType.OnSite,
        signImgUrl: detail.signImageUrl,
        signedAt: detail.signedAt,
        carwash:
          form.carwashType.value === CarwashType.FreeWash ||
          form.carwashType.value === CarwashType.PaidWash,
        carwashType: form.carwashType.value,
        returnType: form.returnType.value,
        oldPartDisposalType: form.oldPartDisposalType.value,
        reservationType: form.reservationType.value,
        reservationId: form.reservationId.value,
        insuranceExpiryDate: form.insuranceExpiryDate.value,
        priority: InspectionOrderPriority.Normal,
        estimatedFinishTime: form.estimatedFinishTime.value
          ? formatDateTime(
              form.estimatedFinishTime.value,
              'yyyy-MM-dd HH:mm:ss',
            )
          : undefined,
        remark: form.remark.value,
        detail: {
          serviceSubjects: detail.serviceSubjects,
          extendedPropDefs:
            storeConfiguration.extendedOrderProperties ?? undefined,
          extendedProps: normalizedExtendedPropertiesFromOrderExtra(
            detail,
            storeConfiguration,
          ),
          troubleLights: detail.preInspection
            ?.filter(x => x.inspectionType === SiteInspectionType.Dashboard)
            .map<TroubleLightInfo>(inspection => ({
              code: inspection.siteCode,
              name: inspection.siteName,
            })),
          facadeRecordMediaCount: detail.preInspection
            ?.filter(x => x.inspectionType === SiteInspectionType.Facade)
            .flatMap(x => x.items.flatMap(t => t.medias)).length,
        },
        preInspection: detail.preInspection?.map(staged =>
          siteInspectionStagedToCommitPayload(staged, {
            flowId: '',
            technicianId: user.user.memberId,
            technicianName: user.user.name,
            inspectionType: staged.inspectionType,
            siteId: staged.siteId,
            siteName: staged.siteName,
          }),
        ),
        vehicleInfo: lastVehicleInfo,
      };
    },
  );
}
